import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Button, PageHeader, Panel } from "@/components/primitives";
import {
  createCheckoutSession,
  createPortalSession,
  getBillingState,
  type BillingState,
} from "@/lib/billing.functions";

export const Route = createFileRoute("/settings_/billing")({
  validateSearch: (search: Record<string, unknown>): { tier?: "gate" | "yard" } =>
    search['tier'] === "gate" || search['tier'] === "yard" ? { tier: search['tier'] } : {},
  head: () => ({
    meta: [
      { title: "Billing — PortDwellr" },
      { name: "description", content: "Your PortDwellr subscription: plan tier, status, renewal date and payment method." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AppShell>
      <BillingPage />
    </AppShell>
  ),
});

const TIERS = [
  { id: "gate", name: "Gate", price: "$149/mo", note: "1 location · up to 40 trucks/day" },
  { id: "yard", name: "Yard", price: "$349/mo", note: "Unlimited trucks · full loop" },
] as const;

function BillingPage() {
  const { tier: tierParam } = Route.useSearch();
  const loadState = useServerFn(getBillingState);
  const checkout = useServerFn(createCheckoutSession);
  const portal = useServerFn(createPortalSession);

  const [state, setState] = useState<BillingState | null>(null);
  const [selected, setSelected] = useState<"gate" | "yard">(tierParam ?? "yard");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void loadState()
      .then((s) => {
        if (active) setState(s);
      })
      .catch((e: unknown) => {
        if (active) setError(e instanceof Error ? e.message : "Could not load billing.");
      });
    return () => {
      active = false;
    };
  }, [loadState]);

  const go = async (fn: () => Promise<{ url: string }>) => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await fn();
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong. Try again.");
      setBusy(false);
    }
  };

  const account = state?.account ?? null;
  const isAdmin = state?.isAdmin ?? false;

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Billing"
        lead="The plan behind the loop — tier, status and renewal. Card changes, upgrades and cancellation all happen in the Stripe billing portal."
      />

      <div className="space-y-10">
        <Panel title="Current subscription">
          <dl className="grid grid-cols-1 border-l border-line sm:grid-cols-3">
            <div className="border-b border-r border-line px-5 py-4">
              <dt className="font-mono text-[11px] uppercase tracking-[0.1em] text-steel-mid">Tier</dt>
              <dd className="mt-1 font-display text-[20px] font-semibold capitalize">{account?.tier ?? "None"}</dd>
            </div>
            <div className="border-b border-r border-line px-5 py-4">
              <dt className="font-mono text-[11px] uppercase tracking-[0.1em] text-steel-mid">Status</dt>
              <dd className="mt-1 font-display text-[20px] font-semibold capitalize">
                {account ? account.status.replace("_", " ") : "Not subscribed"}
              </dd>
            </div>
            <div className="border-b border-r border-line px-5 py-4">
              <dt className="font-mono text-[11px] uppercase tracking-[0.1em] text-steel-mid">Renews</dt>
              <dd className="mt-1 font-mono text-[15px]">
                {account?.currentPeriodEnd
                  ? new Date(account.currentPeriodEnd).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "—"}
              </dd>
            </div>
          </dl>
          <div className="flex flex-wrap items-center gap-4 border-t border-line p-5 md:px-6">
            <Button
              type="button"
              disabled={!isAdmin || busy || !account?.hasSubscription}
              onClick={() => void go(() => portal({}))}
            >
              {busy ? "Working…" : "Manage billing"}
            </Button>
            {!isAdmin ? (
              <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-steel-mid">
                Org admins only
              </span>
            ) : null}
          </div>
        </Panel>

        {!account?.hasSubscription ? (
          <Panel title="Choose a plan">
            <ul className="grid grid-cols-1 border-l border-line md:grid-cols-2">
              {TIERS.map((t) => (
                <li key={t.id} className="border-b border-r border-line px-5 py-5">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="radio"
                      name="tier"
                      value={t.id}
                      checked={selected === t.id}
                      onChange={() => setSelected(t.id)}
                      className="mt-[6px]"
                    />
                    <span>
                      <span className="block font-display text-[18px] font-semibold">
                        {t.name} · {t.price}
                      </span>
                      <span className="mt-1 block font-mono text-[12px] text-ink-soft">{t.note}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-4 border-t border-line p-5 md:px-6">
              <Button
                type="button"
                disabled={!isAdmin || busy}
                onClick={() => void go(() => checkout({ data: { tier: selected } }))}
              >
                {busy ? "Opening checkout…" : `Subscribe to ${selected === "gate" ? "Gate" : "Yard"}`}
              </Button>
              <a
                href="mailto:upton@portdwellr.com"
                className="font-mono text-[11px] uppercase tracking-[0.1em] text-steel underline decoration-signal decoration-2 underline-offset-4"
              >
                Terminal plan — talk to us
              </a>
            </div>
          </Panel>
        ) : null}

        {error ? (
          <p role="alert" className="border border-signal-deep bg-paper-raised px-4 py-3 font-mono text-[12px] text-signal-deep">
            {error}
          </p>
        ) : null}
      </div>
    </>
  );
}
